import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Input } from '../input/input';
import { Box, CircularProgress, IconButton } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

const SuggestionInput = ({
    Suggestions = [],
    handleInputChange,
    name,
    placeholder,
    simple = false
}) => {

    const [focusData, setFocusData] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [filterData, setFilterData] = useState([]);
    const [updatedValue, setUpdatedValue] = useState('');

    const boxRef = useRef(null);
    const inputRef = useRef(null);

    /* ---------------- Update Suggestions ---------------- */
    useEffect(() => {
        setFilterData(Suggestions);
    }, [Suggestions]);

    /* ---------------- Detect Number Safely ---------------- */
    const isNumeric = (value) => /^\d+$/.test(value);

    /* ---------------- Filter Logic ---------------- */
    const checkKeys = (inputValue = '') => {
        return Suggestions.filter((item) => {
            const nameValue = item?.name?.toString().toLowerCase() || '';
            const rollValue = item?.roll?.toString() || '';

            if (isNumeric(inputValue)) {
                return rollValue.includes(inputValue);
            }

            return nameValue.includes(inputValue.toLowerCase());
        });
    };

    /* ---------------- Handle Typing ---------------- */
    const handleChange = (e) => {
        const inputValue = e.target.value;
        setUpdatedValue(inputValue);
        setFilterData(checkKeys(inputValue));
        setFocusData(true);
    };

    /* ---------------- Toggle Dropdown ---------------- */
    const toggleShowAll = () => {
        const newState = !showAll;
        setShowAll(newState);

        if (newState) {
            setFilterData(Suggestions);
            setFocusData(true);
        }
    };

    /* ---------------- Select Item ---------------- */
    const getValue = (selectedItem) => {
        let value = selectedItem?.name || '';

        if (selectedItem?.roll !== undefined) {
            const fakeEvent = {
                target: {
                    value: selectedItem.roll,
                    name
                }
            };

            if (handleInputChange) {
                if (simple) {
                    handleInputChange([name, selectedItem.roll]);
                } else {
                    handleInputChange(fakeEvent);
                }
            }

            value = `${selectedItem.roll}, ${selectedItem.name || ''}, ${selectedItem.Name_P || ''}`;
        }

        setUpdatedValue(value);
        setFocusData(false);
        setShowAll(false);

        inputRef.current?.focus();
    };

    /* ---------------- Close On Outside Click ---------------- */
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (boxRef.current && !boxRef.current.contains(event.target)) {
                setFocusData(false);
                setShowAll(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
            <Input
                ref={inputRef}
                onChange={handleChange}
                onFocus={() => setFocusData(true)}
                name={name}
                placeholder={placeholder}
                value={updatedValue}
                autoComplete="off"
                required
                sx={{ width: '100%' }}
            />

            <IconButton
                onClick={toggleShowAll}
                sx={{
                    position: 'absolute',
                    right: '8px',
                    color: 'action.active'
                }}
            >
                <ArrowDropDownIcon />
            </IconButton>

            {(focusData || showAll) && (
                <Box
                    ref={boxRef}
                    sx={{
                        position: 'absolute',
                        width: '100%',
                        top: '100%',
                        boxShadow: '0px 5px 11px 0px #d7d7d7',
                        zIndex: 6,
                        background: 'white',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        mt: 1
                    }}
                >
                    {filterData.length > 0 ? (
                        filterData.map((item, index) => (
                            <Box
                                key={index}
                                onClick={() => getValue(item)}
                                sx={{
                                    padding: '10px',
                                    cursor: 'pointer',
                                    '&:hover': {
                                        backgroundColor: 'action.hover'
                                    }
                                }}
                            >
                                {item?.roll
                                    ? `${item.roll}, ${item.name || ''}, ${item.Name_P || ''}, [${item.Class || ''}]`
                                    : item?.name}
                            </Box>
                        ))
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                padding: '20px'
                            }}
                        >
                            <CircularProgress size={40} />
                        </Box>
                    )}
                </Box>
            )}
        </Box>
    );
};

SuggestionInput.propTypes = {
    Suggestions: PropTypes.array,
    handleInputChange: PropTypes.func,
    name: PropTypes.string,
    placeholder: PropTypes.string,
    simple: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
};

export default SuggestionInput;
